import axios from 'axios';

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  timeout: 10000,
});

// Track if we're currently refreshing to avoid multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Function to get CSRF token from cookie
const getCsrfTokenFromCookie = () => {
  const cookies = document.cookie.split(';');
  const xsrfCookie = cookies.find(cookie => cookie.trim().startsWith('XSRF-TOKEN='));
  if (xsrfCookie) {
    return xsrfCookie.split('=')[1].trim();
  }
  return null;
};

// Create a separate axios instance for CSRF requests
const csrfAxios = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL}/api`,
  withCredentials: true,
  timeout: 5000,
});

// Function to get fresh CSRF token
const getCsrfToken = async () => {
  try {
    await csrfAxios.get('/auth/csrf');
    return true;
  } catch (error) {
    console.error('Failed to get CSRF token:', error);
    return false;
  }
};

// Function to attempt token refresh
const refreshToken = async () => {
  try {
    await axiosInstance.post('/auth/refresh');
    return true;
  } catch (error) {
    if (error.response && error.response.status !== 401) {
        console.error('Token refresh failed:', error);
    }
    return false;
  }
};

// Request interceptor for CSRF token handling
axiosInstance.interceptors.request.use(
  async (config) => {
    // Skip CSRF handling for these endpoints
    const skipCsrfEndpoints = ['/auth/csrf', '/auth/google-login'];
    if (skipCsrfEndpoints.some(endpoint => config.url === endpoint)) {
      return config;
    }
    
    // For state-changing operations, ensure we have CSRF token
    if (['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase())) {
      let csrfToken = getCsrfTokenFromCookie();
      
      // If no token in cookie, get a fresh one
      if (!csrfToken) {
        await getCsrfToken();
        csrfToken = getCsrfTokenFromCookie();
      }
      
      // Set the token in the header
      if (csrfToken) {
        config.headers['X-XSRF-TOKEN'] = csrfToken;
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor with automatic token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Enhanced error information
    if (error.code === 'ECONNABORTED') {
      error.message = 'Request timeout';
      error.isTimeout = true;
    } else if (error.message === 'Network Error') {
      error.isNetworkError = true;
      error.message = 'Network Error';
    } else if (!error.response) {
      error.isNetworkError = true;
      error.message = error.message || 'Network Error';
    }

    // Handle CSRF errors with retry logic
    if (error.response?.status === 403 && originalRequest && !originalRequest.__isRetryRequest) {
      originalRequest.__isRetryRequest = true;
      
      const tokenRefreshed = await getCsrfToken();
      
      if (tokenRefreshed) {
        const freshToken = getCsrfTokenFromCookie();
        if (freshToken) {
          originalRequest.headers['X-XSRF-TOKEN'] = freshToken;
          return axiosInstance.request(originalRequest);
        }
      }
    }
    
    // Handle 401 errors with automatic token refresh
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Skip refresh attempt for auth endpoints
      const authEndpoints = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'];
      if (authEndpoints.some(endpoint => originalRequest.url?.includes(endpoint))) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          return axiosInstance(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshSuccess = await refreshToken();
        
        if (refreshSuccess) {
          processQueue(null, true);
          isRefreshing = false;
          
          // Retry the original request
          return axiosInstance(originalRequest);
        } else {
          throw new Error('Refresh failed');
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;

        if (originalRequest.url.includes('/auth/me')) {
            return Promise.reject(refreshError);
        }
        
        // Only redirect if not already on login/auth pages
        const authPages = ['/login', '/register', '/forgot-password', '/reset-password'];
        const currentPath = window.location.pathname;
        
        if (!authPages.some(page => currentPath.startsWith(page))) {
          localStorage.removeItem('pendingInvite');
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;